import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './GuessWho.css';

/* Do you know your tribe? — a sentence somebody wrote about themselves, three faces, one guess.

   ★ It has its own page (`/know-your-tribe`). It used to sit ON the community wall, opened by a
   `?quiz=1` query param, and that was two different things sharing one screen: the wall is a
   directory you come to in order to find a person, this is a game you come to in order to play it.
   The wall keeps a one-line door to here, which is all it ever needed.

   ★ The answer is not in this component. The round arrives with a signed token and three names,
   the pick goes back with the token, and the server says which it was — so nothing here can be
   read out of the payload. What it CANNOT stop is somebody opening the three profiles in another
   tab: every sentence asked about is public on /u/<id> by design. That is fine. The points are
   capped and this is a game, not an exam.

   ★ The one number on it is COVERAGE — how many of the faces here you have got right at least
   once. That is the game's own progress and the reason to come back next month; it is not the
   engagement score, which stays on the admin's own screen. A player still never sees a total that
   says what they are worth to their employer, which is the platform's standing rule.

   ★ The wrong answer is the good one: it is the one that teaches you something, so the reveal is
   never a scolding. It keeps the question you were asked in view, says who it was, and what the
   two of you turn out to have in common. */

const GuessWho = ({ onOpenPerson }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);        // {question, reason, played, right, known, total…}
  const [asked, setAsked] = useState(null);      // the question the reveal below belongs to
  const [result, setResult] = useState(null);    // the reveal of the round just answered
  const [busy, setBusy] = useState(false);
  /* The route is not for this viewer at all. ★ On the wall this used to render nothing; on a page
     of its own a blank screen is a bug report, so the reason is shown — and it matters which one,
     because the gate answers `notEntitled` (the company has not bought this) separately from
     `notAuthorized` (you are not on a roster). */
  const [dead, setDead] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/quiz')
      .then(setData)
      .catch((err) => setDead(apiErrorText(t, err, 'notAuthorized')));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const answer = (id) => {
    if (busy || !data || !data.question) return;
    setBusy(true);
    /* ★ Held BEFORE the answer goes out. The response carries the NEXT round, so a moment later
       `data.question` is a different question — and a reveal that says "it was Ann" with no
       question above it leaves you reading an answer to something you can no longer see. */
    const wasAsked = data.question;
    apiRequest('POST', '/company/quiz', { token: data.question.token, pick: id })
      .then((res) => {
        setAsked(wasAsked);
        setResult(res);
        /* The next round rides on the answer, so the reveal and the question after it are one
           round trip. `recognised` comes only on the GET — it is the page's own furniture, not
           part of a round — so it is carried over rather than dropped. */
        setData((prev) => ({ ...res, question: res.question,
                             recognised: (prev && prev.recognised) || [] }));
      })
      .catch(() => setResult({ error: true }))
      .finally(() => setBusy(false));
  };

  /* One this-or-that pick as the question it answers and the answer given. ★ The question half is
     the whole point: "Coffee · Night owl · Outdoors" is a list of words, and only "Coffee or tea →
     Coffee" is somebody telling you something about themselves. */
  const pair = (value) => {
    const [key, pick] = String(value).split(':');
    return { q: t(`pfTot_${key}`), a: t(`pfTotv_${pick}`) };
  };

  const statement = (q) => {
    if (q.text) return <p className="gw-statement">{q.text}</p>;
    if (q.field === 'this_or_that') {
      return (
        <ul className="gw-pairs">
          {q.values.map((value) => (
            <li className="gw-pair" key={value}>
              <span className="gw-pair__q">{pair(value).q}</span>
              <span className="gw-pair__a">{pair(value).a}</span>
            </li>
          ))}
        </ul>
      );
    }
    return <p className="gw-statement">{q.values.join(' · ')}</p>;
  };

  /* What the two of you turn out to have in common — a sentence PER FIELD, not a bare list.
     "You both said: english" was the payoff line of this whole feature saying nothing about what
     was shared: a value with no question behind it, which is the same mistake the this-or-that
     statement used to make. "You both speak english" needs no explaining.

     Grouped so two hobbies are one line rather than two, and ordered by whatever the server sent
     (most personal first).

     ★ `field`, not `key`. The server emits {field, value} (routes/community_quiz._shared); this
     read `s.key` — copied from the wall's own helper, which really does return {key, value} — so
     the branch never fired and a shared this-or-that rendered as the raw token `drink:coffee`. */
  const sharedLines = (shared) => {
    const byField = [];
    (shared || []).forEach((s) => {
      const text = s.field === 'this_or_that' ? `${pair(s.value).q}: ${pair(s.value).a}` : s.value;
      const line = byField.find((l) => l.field === s.field);
      if (line) line.values.push(text); else byField.push({ field: s.field, values: [text] });
    });
    return byField.map((l) => ({
      field: l.field,
      text: t(`quizShared_${l.field}`,
              { what: l.values.join(l.field === 'this_or_that' ? ' · ' : ', ') }),
    }));
  };

  if (dead) return <p className="bravos-cal__notice">{dead}</p>;

  if (data === null) {
    return <div className="gw"><span className="auth__spinner" /></div>;
  }

  const q = data.question;
  const reason = data.reason;
  const recognised = data.recognised || [];

  return (
    <div className="gw">
      {/* How this player is doing. The fraction is the one worth coming back for — it is the
          sentence "I know my tribe better than I did last month" as a number — and it is never a
          total of anything an employer reads. */}
      {data.total > 0 && (
        <p className="gw-known">
          <Icon name="users" className="me-1" />
          {t('quizKnown', { known: data.known || 0, total: data.total })}
        </p>
      )}
      {data.played > 0 && (
        <p className="gw-score">
          {t('quizScore', { right: data.right, played: data.played })}
          {data.streak > 1 && <span className="gw-streak">{t('quizStreak', { n: data.streak })}</span>}
        </p>
      )}

      {/* ★ `result.answer` guards the `gone` shape: when the person left the roster between the
          two halves of a round the server answers with the next question and no answer at all,
          and that response has no `error` on it either — reading `.photo_url` off it was a blank
          screen for something the player did nothing about. */}
      {result && !result.error && result.answer && (
        <div className={`gw-reveal ${result.correct ? 'is-right' : ''}`}>
          <img className="gw-reveal__face" src={result.answer.photo_url} alt=""
               onClick={() => onOpenPerson && onOpenPerson(result.answer.id)} />
          <div className="gw-reveal__text">
            {/* What you were actually asked, kept in view. */}
            {asked && (
              <span className="gw-reveal__asked">
                {t(`quizAsk_${asked.field}`)}
                {asked.text ? ` ${asked.text}`
                  : ` ${asked.field === 'this_or_that'
                    ? asked.values.map((v) => `${pair(v).q}: ${pair(v).a}`).join(' · ')
                    : asked.values.join(' · ')}`}
              </span>
            )}
            <strong>{result.correct ? t('quizRight') : t('quizWrong')}</strong>
            <span>{t('quizItWas', { name: result.answer.name })}</span>
            {/* The payoff, and the reason being wrong is worth something. */}
            {sharedLines(result.shared).map((line) => (
              <span className="gw-reveal__shared" key={line.field}>{line.text}</span>
            ))}
          </div>
        </div>
      )}

      {result && result.error && <p className="bravos-cal__notice">{t('somethingWentWrong')}</p>}

      {!q && (
        <p className="ce-note">
          {reason === 'tooFewPeople' ? t('quizTooFewPeople') : t('quizNothingSaid')}
        </p>
      )}

      {q && (
        <>
          <p className="gw-ask">{t(`quizAsk_${q.field}`)}</p>
          {statement(q)}

          <div className="gw-options">
            {q.options.map((option) => (
              <button
                key={option.id}
                type="button"
                className="gw-option"
                disabled={busy}
                onClick={() => answer(option.id)}
              >
                <img className="gw-option__img" src={option.photo_url} alt="" loading="lazy" />
                <span className="gw-option__name">{option.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Who this tribe knows best. ★ Faces and names, never the tallies behind them: "everybody
          knows Ann" is a warm thing to say about a place, and the same list with a number beside
          each name is a popularity scoreboard, which is not. */}
      {recognised.length > 0 && (
        <div className="gw-known-best">
          <p className="gw-ask">{t('quizRecognised')}</p>
          <p className="ce-note gw-known-best__note">{t('quizRecognisedNote')}</p>
          <div className="gw-faces">
            {recognised.map((person) => (
              <button key={person.id} type="button" className="gw-face"
                      onClick={() => onOpenPerson && onOpenPerson(person.id)}>
                <img className="gw-face__img" src={person.photo_url} alt="" loading="lazy" />
                <span className="gw-face__name">{person.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuessWho;
